import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { nanoid } from "nanoid";
import cx from "classnames";
import { useLanguage } from "hooks/api/language";
import { useAddGroup, useEditGroup } from "hooks/api/groups";
import { useStudents } from "hooks/api/students";
import { useTeachersByLanguage } from "hooks/api/teachers";
import { Input } from "components/Interactive/Input";
import { Select } from "components/Interactive/Select";
import { PersonSelect } from "components/Interactive/PersonSelect";
import { Button } from "components/Interactive/Button";
import { Avatar } from "components/Common/Avatar";
import { ReactPortal } from "components/Common/ReactPortal";
import { Space } from "components/Common/Space";
import { GroupsContent } from "content";
import { logSuccess } from "utils/notifications";
import { TTeacher } from "types/api/teacher";
import { TStudent } from "types/api/student";
import { ELanguageLevel } from "types/api/language";
import { TGroup } from "types/api/groups";
import { TPersonSelectOption, TSelectOption } from "types/app/select";
import { TAddGroupForm } from "./AddGroupForm.types";
import "./AddGroupForm.styles.scss";

type AddGroupFormProps = {
  /**
   * Callback function fires on add group submit event.
   */
  onSubmit?: () => void;
  /**
   * Override or extend the styles applied to the component.
   */
  className?: string;
  /**
   * Group data for editing mode. If provided, the form will be in edit mode.
   */
  initialData?: TGroup;
  /**
   * If true, the form is in edit mode.
   */
  isEditMode?: boolean;
};

export const AddGroupForm: React.FC<AddGroupFormProps> = (props) => {
  const { className, onSubmit, initialData, isEditMode = false } = props;

  const [studentSearchValue, setStudentSearchValue] = useState<string>("");
  const idRef = useRef<string>(nanoid());
  const hasInitializedForm = useRef<boolean>(false);

  const { mutate: addGroupHandler } = useAddGroup();
  const { mutate: editGroupHandler } = useEditGroup();
  const { data: studentsData } = useStudents({
    searchText: studentSearchValue,
    pageSize: 99999, // Get all students for group editing
  });
  const { data: languageData, isSuccess: isLanguageSuccess } = useLanguage();

  const studentsContent: TStudent[] = useMemo(
    () => studentsData?.content || [],
    [studentsData?.content]
  );
  const students: TPersonSelectOption[] = studentsContent.map((student) => ({
    id: student.id,
    name: `${student.person.firstName} ${student.person.lastName}`,
    description: student.person.email,
  }));

  const languageOptions: TSelectOption[] = useMemo(
    () =>
      languageData?.map((lang) => ({
        label: lang,
        value: lang,
      })) || [],
    [languageData]
  );
  const [initialLanguageOption] = languageOptions;
  const levelOptions: TSelectOption<ELanguageLevel>[] = Object.entries(
    ELanguageLevel
  ).map(([key, level]) => ({
    label: key,
    value: level,
  }));
  const [initialLevelOption] = levelOptions;

  // Transform initial data for editing mode
  const getInitialFormData = useMemo(() => {
    if (!isEditMode || !initialData) {
      return {
        languageLevel: initialLevelOption,
      };
    }

    return {
      name: initialData.name,
      language: languageOptions.find(
        (opt) => opt.value === initialData.language
      ),
      languageLevel:
        levelOptions.find((opt) => opt.value === initialData.level) ||
        initialLevelOption,
      teachers: [], // Will be populated when teachers data is loaded
      members: [], // Will be populated when students data is loaded
    };
  }, [
    isEditMode,
    initialData,
    languageOptions,
    levelOptions,
    initialLevelOption,
  ]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, touchedFields },
  } = useForm<TAddGroupForm>({
    mode: "onBlur",
    defaultValues: getInitialFormData,
  });

  useEffect(() => {
    const isLanguageFieldTouched = !!touchedFields.language;
    if (isLanguageSuccess && !isLanguageFieldTouched && !isEditMode) {
      setValue("language", initialLanguageOption);
    }
  }, [
    isLanguageSuccess,
    touchedFields.language,
    initialLanguageOption,
    setValue,
    isEditMode,
  ]);

  // Set initial form values when in edit mode and data is available (only once)
  useEffect(() => {
    if (
      isEditMode &&
      initialData &&
      languageOptions.length > 0 &&
      !hasInitializedForm.current
    ) {
      const languageOption = languageOptions.find(
        (opt) => opt.value === initialData.language
      );
      const levelOption =
        levelOptions.find((opt) => opt.value === initialData.level) ||
        initialLevelOption;

      // Use reset to properly initialize the form with all values
      reset({
        name: initialData.name,
        language: languageOption,
        languageLevel: levelOption,
        teachers: [], // Will be populated in separate effects
        members: [], // Will be populated in separate effects
      });

      hasInitializedForm.current = true;
    }
  }, [
    isEditMode,
    initialData,
    languageOptions,
    levelOptions,
    initialLevelOption,
    reset,
  ]);

  // Reset the initialization flag when the modal closes/opens or when switching between modes
  useEffect(() => {
    if (!isEditMode || !initialData) {
      hasInitializedForm.current = false;
    }
  }, [isEditMode, initialData]);

  const currentLanguage = watch("language");

  const { data: teachersData } = useTeachersByLanguage(currentLanguage?.value);
  const teachers: TTeacher[] = useMemo(
    () => teachersData?.content || [],
    [teachersData?.content]
  );
  // Transform teachers to PersonSelect format
  const transformTeachersToPersonSelectOptions = (
    teachers: TTeacher[]
  ): TPersonSelectOption[] => {
    return teachers.map((teacher) => ({
      id: teacher.id,
      name: `${teacher.person.firstName} ${teacher.person.lastName}`,
      description: teacher.person.email || "",
    }));
  };

  // Populate teachers and members when in edit mode and data is loaded
  useEffect(() => {
    if (isEditMode && initialData && teachers.length > 0) {
      const selectedTeachers = teachers
        .filter((teacher) => initialData.teacherIds.includes(teacher.id))
        .map((teacher) => ({
          id: teacher.id,
          name: `${teacher.person.firstName} ${teacher.person.lastName}`,
          description: teacher.person.email || "",
        }));
      setValue("teachers", selectedTeachers);
    }
  }, [isEditMode, initialData, teachers, setValue]);

  useEffect(() => {
    if (isEditMode && initialData && studentsContent.length > 0) {
      const selectedStudents = studentsContent
        .filter((student) => initialData.memberIds.includes(student.id))
        .map((student) => ({
          id: student.id,
          name: `${student.person.firstName} ${student.person.lastName}`,
          description: student.person.email,
        }));
      setValue("members", selectedStudents);
    }
  }, [isEditMode, initialData, studentsContent, setValue]);

  // Search callback for PersonSelect
  const searchTeachers = async (
    searchValue: string
  ): Promise<TPersonSelectOption[]> => {
    const teacherOptions = transformTeachersToPersonSelectOptions(teachers);

    if (!searchValue) {
      return teacherOptions;
    }

    return teacherOptions.filter(
      (option) =>
        option.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.description.toLowerCase().includes(searchValue.toLowerCase())
    );
  };

  const handleAddGroupSubmit = (formData: TAddGroupForm) => {
    const groupData = {
      name: formData.name,
      teacherIds: formData.teachers.map((teacher) => teacher.id),
      language: formData.language.value,
      level: formData.languageLevel.value,
      memberIds: formData.members.map((member) => member.id),
    };

    if (isEditMode && initialData) {
      // Update existing group
      const updatedGroup = {
        ...initialData,
        ...groupData,
      };

      editGroupHandler(updatedGroup, {
        onSuccess: () => {
          onSubmit?.();
          logSuccess(GroupsContent.Edit.Notification.SUCCESS);
        },
      });
    } else {
      // Add new group
      addGroupHandler(groupData, {
        onSuccess: () => {
          onSubmit?.();
          logSuccess(GroupsContent.Add.Notification.SUCCESS);
          reset({
            language: initialLanguageOption,
            languageLevel: initialLevelOption,
            members: [],
            teachers: [],
            name: "",
          });
        },
      });
    }
  };

  const studentsSearchCallback = useCallback(
    async (): Promise<TPersonSelectOption[]> => Promise.resolve(students),
    [students]
  );

  const studentsSearchHandler = (searchValue: string) => {
    setStudentSearchValue(searchValue);
  };

  return (
    <form
      id={idRef.current}
      className={cx(["nb-sub-add-group-form", className])}
      onSubmit={handleSubmit(handleAddGroupSubmit)}
    >
      <div className="nb-sub-add-group-first-row">
        {/* TODO: Replace Avatar placeholder with photo upload when backend will be ready */}
        <Avatar
          bordered
          size="large"
          emoji={GroupsContent.Add.EMOJI}
          className="nb-sub-add-group-avatar"
        />
        <Input<TAddGroupForm>
          name="name"
          id="add-group-name"
          className="nb-sub-add-group-name-input"
          label={GroupsContent.Add.Input.Name.LABEL}
          placeholder={GroupsContent.Add.Input.Name.PLACEHOLDER}
          register={register}
          errors={errors}
          autoFocus
          required
          fullWidth
        />
      </div>
      <PersonSelect<TAddGroupForm, TPersonSelectOption, true>
        name="teachers"
        id="add-group-teachers"
        label={GroupsContent.Add.Input.Teacher.LABEL}
        placeholder={GroupsContent.Add.Input.Teacher.PLACEHOLDER}
        searchCallback={searchTeachers}
        defaultOptions={transformTeachersToPersonSelectOptions(teachers)}
        control={control}
        errors={errors}
        multi={true}
        menuAlwaysOpen
        required
        fullWidth
      />
      <Select<TAddGroupForm, TSelectOption>
        name="language"
        id="add-group-language"
        label={GroupsContent.Add.Input.Language.LABEL}
        placeholder={GroupsContent.Add.Input.Language.PLACEHOLDER}
        options={languageOptions}
        control={control}
        errors={errors}
        required
        fullWidth
      />
      <Select<TAddGroupForm, TSelectOption<ELanguageLevel>>
        name="languageLevel"
        id="add-group-level"
        label={GroupsContent.Add.Input.Level.LABEL}
        placeholder={GroupsContent.Add.Input.Level.PLACEHOLDER}
        options={levelOptions}
        control={control}
        errors={errors}
        required
        fullWidth
      />
      <PersonSelect<TAddGroupForm, TPersonSelectOption, true>
        name="members"
        id="add-group-members"
        label={GroupsContent.Add.Input.Participants.LABEL}
        placeholder={GroupsContent.Add.Input.Participants.PLACEHOLDER}
        searchCallback={studentsSearchCallback}
        onSearchChange={studentsSearchHandler}
        control={control}
        errors={errors}
        defaultOptions={students}
        multi
        menuAlwaysOpen
        required
        fullWidth
      />
      <Space size="medium" />
      <ReactPortal
        wrapperId={
          isEditMode ? "edit-group-modal-footer" : "add-group-modal-footer"
        }
      >
        <Button variant="primary" type="submit" form={idRef.current} fullWidth>
          {isEditMode ? GroupsContent.Edit.SUBMIT : GroupsContent.Add.SUBMIT}
        </Button>
      </ReactPortal>
    </form>
  );
};
